import {Component, computed, inject, OnInit, signal, input, output} from '@angular/core';

import {ReactiveFormsModule} from '@angular/forms';
import {TranslocoPipe, TranslocoService} from '@jsverse/transloco';
import {Divider} from 'primeng/divider';
import {TooltipModule} from 'primeng/tooltip';
import {RichTextEditorComponent} from '../../../../../../../shared/components/rich-text-editor/rich-text-editor.component';
import {DirectusSelectComponent} from '../../../../../../../shared/components/inputs/directus-select/directus-select.component';
import {FixedValueService} from '../../../../../../../shared/services/fixed-value.service';
import {DirectusSelect} from '@frontend/directus';
import {
  ProjectCharterOverviewResponse,
  ProjectCharterOverviewUpsertRequest,
} from '@frontend/shared-api';
import {switchMap} from 'rxjs/operators';
import {finalize, Observable} from 'rxjs';
import {ToastService} from '@frontend/toast';
import {Step12ProjectCharterService} from '../step-1-2-project-charter.service';
import {
  ProjectStepRichTextService,
  RichTextSectionConfig,
} from '../../../../../../../shared/services/project-step-rich-text.service';
import {TabStatus} from '../../../../../../../shared/tabs/tab-status.model';
import {RichTextSection} from '../../../../../../../shared/model/rich-text-editor.model';
import {ProjectBaseStepComponent} from '../../../../project-base-step/project-base-step.component';
import {ProjectStepSaveButtonComponent} from '../../../../../../../shared/project-step-save-button/project-step-save-button.component';
import {SECTION_KEYS} from '../../../../../../../shared/models/charter.types';

interface DropdownSelection {
  value: string | null;
  dropdown: DirectusSelect | null;
  originalDropdown: DirectusSelect | null;
  hasChanges: boolean;
}

interface DropdownConfig {
  id: DropdownId;
  labelKey: string;
  data: DropdownSelection;
}

type CharterField = Exclude<keyof ProjectCharterOverviewUpsertRequest, 'projectId'>;
type DropdownId = (typeof DROPDOWN_IDS)[number];

@Component({
  selector: 'app-step-1-2-project-chart-overview',
  providers: [ProjectStepRichTextService],
  imports: [
    ReactiveFormsModule,
    TranslocoPipe,
    Divider,
    TooltipModule,
    RichTextEditorComponent,
    DirectusSelectComponent,
    ProjectStepSaveButtonComponent,
  ],
  templateUrl: './step-1-2-project-charter-overview.component.html',
})
export class Step12ProjectCharterOverviewComponent
  extends ProjectBaseStepComponent
  implements OnInit
{
  private readonly chartService = inject(Step12ProjectCharterService);
  private readonly fixedValueService = inject(FixedValueService);
  private readonly toast = inject(ToastService);
  private readonly transloco = inject(TranslocoService);
  readonly richTextService = inject(
    ProjectStepRichTextService<ProjectCharterOverviewUpsertRequest, ProjectCharterOverviewResponse>,
  );

  private readonly dropdownData = signal<Map<DropdownId, DropdownSelection>>(
    new Map(
      DROPDOWN_IDS.map((id) => [
        id,
        {value: null, dropdown: null, originalDropdown: null, hasChanges: false},
      ]),
    ),
  );
  dropdownCollectionName = 'project_value';
  savingStates = signal<Map<string, boolean>>(new Map());

  readonly preloadedData = input<ProjectCharterOverviewResponse>();
  readonly statusChange = output<TabStatus>();

  onStatusChange = () => {
    this.emitStatusChange();
  };

  get companyDescriptionSections() {
    return signal(
      this.richTextService
        .getSections()()
        .filter(
          (s) =>
            s.key.includes('company_profile') ||
            s.key.includes('company_core') ||
            s.key.includes('company_long_term'),
        ),
    );
  }

  get additionalSections() {
    return signal(
      this.richTextService
        .getSections()()
        .filter(
          (s) => s.key.includes('project_rationale') || s.key.includes('project_description'),
        ),
    );
  }

  readonly dropdowns = computed<DropdownConfig[]>(() =>
    DROPDOWN_IDS.map((id) => {
      const data = this.dropdownData().get(id) ?? {
        value: null,
        dropdown: null,
        originalDropdown: null,
        hasChanges: false,
      };
      return {id, labelKey: `project.steps.${id}`, data};
    }),
  );

  readonly canSaveDropdowns = computed(() => {
    const values = Array.from(this.dropdownData().values());
    return values.every((d) => d.value) && values.some((d) => d.hasChanges);
  });

  override ngOnInit(): void {
    super.ngOnInit();
    this.initializeRichTextService();
    this.loadDropdownData();
  }

  private initializeRichTextService(): void {
    const config: RichTextSectionConfig<
      ProjectCharterOverviewUpsertRequest,
      ProjectCharterOverviewResponse
    > = {
      sections: ALL_SECTIONS,
      fieldMap: OVERVIEW_FIELD_MAP,
      saveFunction: (projectId, payload) => this.saveWithCompleteDto(projectId, payload),
      loadFunction: (projectId) => this.chartService.getCharterOverview(projectId),
    };

    this.richTextService.initialize(this.projectId, config, this.preloadedData());
  }

  private loadDropdownData(): void {
    this.fixedValueService
      .loadOrInitializeMultipleFixedCollections(this.projectId, [this.dropdownCollectionName])
      .subscribe(() => this.loadDropdownValues());
  }

  private loadDropdownValues(): void {
    if (!this.projectId) return;
    this.fixedValueService
      .loadOrInitializeFixedDropdownValues(this.projectId, this.dropdownCollectionName)
      .subscribe((dropdown) => {
        this.dropdownData.update((current) => {
          const m = new Map(current);
          DROPDOWN_IDS.forEach((id) => {
            const info = m.get(id);
            if (info) {
              m.set(id, {...info, dropdown, originalDropdown: dropdown});
            }
          });
          return m;
        });
        this.loadDropdownValuesFromCharter();
      });
  }

  private loadDropdownValuesFromCharter(): void {
    if (!this.projectId) return;
    this.chartService.getCharterOverview(this.projectId).subscribe((charter) => {
      this.dropdownData.update((current) => {
        const m = new Map(current);
        for (const id of DROPDOWN_IDS) {
          const info = m.get(id);
          if (info) {
            const field = this.dropdownKeyToField(id);
            m.set(id, {...info, value: (charter[field] as string) ?? null, hasChanges: false});
          }
        }
        return m;
      });
      this.emitStatusChange();
    });
  }

  onDropdownChange(id: DropdownId, value: string): void {
    this.dropdownData.update((current) => {
      const m = new Map(current);
      const info = m.get(id);
      if (info) m.set(id, {...info, value, hasChanges: true});
      return m;
    });
    this.updateDropdownFilters();
  }

  private updateDropdownFilters(): void {
    const current = this.dropdownData();
    const selected = Array.from(current.values())
      .map((d) => d.value)
      .filter((value): value is string => value !== null && value !== undefined);
    const original = current.get(DROPDOWN_IDS[0])?.originalDropdown;
    if (!original) return;
    const all = original.values ?? [];

    this.dropdownData.update(() => {
      const m = new Map<DropdownId, DropdownSelection>();
      for (const id of DROPDOWN_IDS) {
        const info = current.get(id);
        if (info) {
          const filtered = all.filter((v) => v.id === info.value || !selected.includes(v.id));
          m.set(id, {...info, dropdown: {...original, values: filtered}});
        }
      }
      return m;
    });
  }

  saveDropdowns(): void {
    if (!this.canSaveDropdowns()) return;
    this.setSaving('dropdowns', true);

    const payload = this.buildCompleteOverviewPayload();

    this.dropdownData().forEach((info, id) => {
      payload[this.dropdownKeyToField(id)] = info.value ?? undefined;
    });

    this.chartService
      .saveCharterOverview(this.projectId, payload)
      .pipe(finalize(() => this.setSaving('dropdowns', false)))
      .subscribe(() => {
        this.toast.showSuccess(
          this.transloco.translate('common.success'),
          this.transloco.translate('project.steps.sectionSaved'),
        );
        this.stepState.loadStepStatuses(this.projectId);
        this.dropdownData.update((map) => {
          const m = new Map(map);
          m.forEach((i, id) => m.set(id, {...i, hasChanges: false}));
          return m;
        });
        this.emitStatusChange();
      });
  }

  isSaving(key: string): boolean {
    if (key === 'dropdowns') {
      return this.savingStates().get(key) ?? false;
    }
    return this.richTextService.isSaving(key);
  }

  private setSaving(key: string, saving: boolean): void {
    this.savingStates.update((states) => {
      const m = new Map(states);
      m.set(key, saving);
      return m;
    });
  }

  private dropdownKeyToField(id: DropdownId): CharterField {
    return DROPDOWN_FIELD_MAP[id];
  }

  private saveWithCompleteDto(
    projectId: number,
    richTextPayload: ProjectCharterOverviewUpsertRequest,
  ): Observable<ProjectCharterOverviewResponse> {
    return this.chartService.getCharterOverview(projectId).pipe(
      switchMap((existingData) => {
        const completePayload: ProjectCharterOverviewUpsertRequest = {
          ...existingData,
          ...richTextPayload,
        };
        return this.chartService.saveCharterOverview(projectId, completePayload);
      }),
    );
  }

  private buildCompleteOverviewPayload(): ProjectCharterOverviewUpsertRequest {
    const payload: ProjectCharterOverviewUpsertRequest = {};

    const currentSections = this.richTextService.getSections()();
    currentSections.forEach((section) => {
      const field = OVERVIEW_FIELD_MAP[section.key] as keyof ProjectCharterOverviewUpsertRequest;
      if (field) {
        payload[field] = section.content;
      }
    });

    return payload;
  }

  protected emitStatusChange(): void {
    const richTextCompleted = this.richTextService.getCompletedSectionsCount();
    let dropdownCompleted = 0;

    this.dropdownData().forEach((dropdown) => {
      if (dropdown.value && dropdown.value.trim() !== '') {
        dropdownCompleted++;
      }
    });

    const totalSections = this.richTextService.getTotalSectionsCount() + DROPDOWN_IDS.length;
    const completedSections = richTextCompleted + dropdownCompleted;

    this.statusChange.emit({totalSections, completedSections});
  }
}

const DROPDOWN_IDS = ['dropdown1', 'dropdown2', 'dropdown3'] as const;

const COMPANY_DESCRIPTION_SECTIONS: RichTextSection[] = [
  {
    key: SECTION_KEYS.COMPANY_PROFILE,
    content: '',
    tooltipKey: 'project.steps.projectChart.companyProfile',
    hasChanges: false,
  },
  {
    key: SECTION_KEYS.COMPANY_CORE_PRODUCTS,
    content: '',
    tooltipKey: 'project.steps.projectChart.companyCoreProduct',
    hasChanges: false,
  },
  {
    key: SECTION_KEYS.COMPANY_LONG_TERM_GOALS,
    content: '',
    tooltipKey: 'project.steps.projectChart.companyLongTermGoals',
    hasChanges: false,
  },
];

const ADDITIONAL_SECTIONS: RichTextSection[] = [
  {
    key: SECTION_KEYS.PROJECT_RATIONALE,
    content: '',
    tooltipKey: 'project.steps.projectChart.projectRationale',
    hasChanges: false,
  },
  {
    key: SECTION_KEYS.PROJECT_DESCRIPTION,
    content: '',
    tooltipKey: 'project.steps.projectChart.projectDescription',
    hasChanges: false,
  },
];

export const ALL_SECTIONS: RichTextSection[] = [
  ...COMPANY_DESCRIPTION_SECTIONS,
  ...ADDITIONAL_SECTIONS,
];

export const OVERVIEW_FIELD_MAP: Record<string, CharterField> = {
  [SECTION_KEYS.COMPANY_PROFILE]: 'companyProfile',
  [SECTION_KEYS.COMPANY_CORE_PRODUCTS]: 'companyCoreProducts',
  [SECTION_KEYS.COMPANY_LONG_TERM_GOALS]: 'companiesLongTermGoals',
  [SECTION_KEYS.PROJECT_RATIONALE]: 'projectRationale',
  [SECTION_KEYS.PROJECT_DESCRIPTION]: 'projectDescription',
};

const DROPDOWN_FIELD_MAP: Record<string, CharterField> = {
  dropdown1: 'companyValue1',
  dropdown2: 'companyValue2',
  dropdown3: 'companyValue3',
};
